import * as React from 'react';

import {
    CHARMS_USAGE_GETDEFAULTSET,
    CHARMS_USAGE_SOURCE_CALENDARPICKERCONTEXTMENU,
    CharmInfo,
    areDefaultCharmsAvailable,
    charmsDatapoints,
    getCharmForId,
} from 'owa-calendar-charms-store';
import { getCalendarGroups, getCalendarEntries } from 'owa-calendar-cache';
import type {
    CalendarEntry,
    CalendarGroup,
    CalendarId,
    LocalCalendarEntry,
} from 'owa-graph-schema';
import {
    ContextualMenu,
    ContextualMenuItemType,
    DirectionalHint,
    IContextualMenuItem,
} from '@fluentui/react/lib/ContextualMenu';
import {
    changeCalendarColorString,
    moveDownText,
    moveUpText,
    newBirthdayString,
    renameCalendarString,
    showThisOnlyString,
} from './CalendarPickerCalendarContextMenu.locstring.json';
import { setCalendarCharm, setCalendarColor } from '../actions/calendarActions';

import CalendarFolderTypeEnum from 'owa-service/lib/contract/CalendarFolderTypeEnum';
import CharmDefaultSet from 'owa-charmpicker/lib/components/CharmDefaultSet';
import ColorPicker from './ColorPicker';
import type { ColorScheme } from 'owa-color-utils/lib/ColorScheme';
import type ContextMenuViewState from '../store/schema/ContextMenuViewState';
import { addBirthdayEventMenuAction } from 'owa-event-context-menu';
import { charm as charm_1 } from 'owa-locstrings/lib/strings/charm.locstring.json';
import { getCalendarColorScheme } from 'owa-calendar-color-resolver';
import { getRemoveDeleteConfirmationText } from '../index';
import { isFeatureEnabled } from 'owa-feature-flags';
import { lazyShowCalendarSharingPermissionsDialog } from 'owa-calendar-sharing';
import loc from 'owa-localize';
import { logUsage } from 'owa-analytics';
import { moveTo } from 'owa-locstrings/lib/strings/moveto.locstring.json';
import { observer } from 'mobx-react-lite';
import { shareCalendarDialogTitle } from 'owa-locstrings/lib/strings/sharecalendardialogtitle.locstring.json';
import { updateCalendarDisplayOrder } from 'owa-calendar-update';
import { useComputed } from 'owa-react-hooks/lib/useComputed';
import {
    canRenameCalendar,
    canDeleteCalendar,
    canChangeCalendarColor,
    canChangeCalendarCharm,
} from 'owa-calendar-properties';

import styles from './CalendarPickerCalendarContextMenu.scss';

export interface CalendarPickerCalendarContextMenuProps {
    calendarEntry: CalendarEntry;
    availableColors: ColorScheme[];
    contextMenuViewState: ContextMenuViewState;
    prevCalendarId?: string;
    nextCalendarId?: string;
    onDismiss: (ev?: any) => void;
    onRename: (ev?: any) => void;
    onRemove: (ev?: any) => void;
    onNewColorChosen: () => void;
    onShowThisOnly: () => void;
}

export default observer(function CalendarPickerCalendarContextMenu(
    props: CalendarPickerCalendarContextMenuProps
) {
    const populateMenuItems = useComputed((): IContextualMenuItem[] => {
        let {
            calendarEntry,
            prevCalendarId,
            nextCalendarId,
            onRename,
            onRemove,
            onShowThisOnly,
        } = props;
        const selectedColorScheme = getCurrentColorScheme();
        const localCalendar: LocalCalendarEntry = calendarEntry as LocalCalendarEntry;
        let menuItems: IContextualMenuItem[] = [];
        const confirmationText = getRemoveDeleteConfirmationText(localCalendar);
        const calendarGroups = getCalendarGroupsForSubMenu();

        // 'Show this calendar only' context menu option
        if (!localCalendar.IsGroupMailboxCalendar) {
            // Show this only
            menuItems.push({
                key: loc(showThisOnlyString),
                name: loc(showThisOnlyString),
                onClick: onShowThisOnly,
            });

            menuItems.push({
                key: 'calendar-showThisOnlyDivider',
                itemType: ContextualMenuItemType.Divider,
            });
        }

        // Add New Birthday button for Birthday Calendar
        if (
            calendarEntry &&
            calendarEntry.CalendarFolderType == CalendarFolderTypeEnum.BirthdayCalendar
        ) {
            menuItems.push({
                key: loc(newBirthdayString),
                name: loc(newBirthdayString),
                onClick: renderContactsForms,
            });
        }

        // Sharing Permissions
        if (calendarEntry.CanShare) {
            let folderId = localCalendar.FolderId.Id;
            const calendarId: CalendarId = {
                id: localCalendar.calendarId.id,
                changeKey: localCalendar.calendarId.changeKey,
                mailboxInfo: localCalendar.calendarId.mailboxInfo,
            };
            menuItems.push({
                key: loc(shareCalendarDialogTitle),
                name: loc(shareCalendarDialogTitle),
                onClick: () => {
                    lazyShowCalendarSharingPermissionsDialog.importAndExecute(
                        folderId,
                        calendarId.id,
                        calendarId.mailboxInfo,
                        getCalendarColorScheme(calendarId).calPrimary,
                        calendarEntry.CalendarName
                    );
                },
            });
        }
        if (canRenameCalendar(calendarEntry)) {
            // Rename
            menuItems.push({
                key: loc(renameCalendarString),
                name: loc(renameCalendarString),
                onClick: onRename,
            });
        }
        if (canDeleteCalendar(calendarEntry)) {
            // Remove
            menuItems.push({
                key: loc(confirmationText.label),
                name: loc(confirmationText.label),
                onClick: onRemove,
            });
        }

        if (canChangeCalendarColor(localCalendar)) {
            // Color
            menuItems.push({
                key: loc(changeCalendarColorString),
                name: loc(changeCalendarColorString),
                subMenuProps: {
                    items: [
                        {
                            key: 'colorPicker',
                            onRender: renderColorPicker(calendarEntry, selectedColorScheme),
                        },
                    ],
                },
            });
        }
        let isCharmPickerButtonEnabled = areDefaultCharmsAvailable();
        if (canChangeCalendarCharm(localCalendar) && isCharmPickerButtonEnabled) {
            menuItems.push({
                key: loc(charm_1),
                name: loc(charm_1),
                subMenuProps: {
                    items: [
                        {
                            key: 'charmPicker',
                            name: 'charmPicker',
                            onRender: renderCharmPicker,
                        },
                    ],
                },
            });
        }

        // Calendar reordering options in the context menu
        if (
            isFeatureEnabled('cal-calendarPickerReorder') &&
            calendarEntry.calendarId.mailboxInfo.type === 'UserMailbox'
        ) {
            menuItems.push({
                key: 'calendar-reorderDivider',
                itemType: ContextualMenuItemType.Divider,
            });

            menuItems.push({
                key: loc(moveUpText),
                name: loc(moveUpText),
                disabled: !prevCalendarId,
                onClick: onCalendarMoveUp,
            });

            menuItems.push({
                key: loc(moveDownText),
                name: loc(moveDownText),
                disabled: !nextCalendarId,
                onClick: onCalendarMoveDown,
            });

            menuItems.push({
                key: loc(moveTo),
                name: loc(moveTo),
                disabled: calendarGroups.length === 0,
                subMenuProps: {
                    items: renderCalendarGroupPicker(calendarGroups),
                },
            });
        }

        return menuItems;
    });
    const renderColorPicker = (calendarEntry: CalendarEntry, colorScheme: ColorScheme) => (
        e: React.MouseEvent<HTMLButtonElement>
    ) => {
        let selectedColorScheme = calendarEntry.CalendarColor > -1 ? colorScheme : null;
        return (
            <ColorPicker
                key="colorPicker"
                selectedScheme={selectedColorScheme}
                selectCalendarColor={onColorClicked}
                selectHexCalendarColor={onHexColorClicked}
                availableColors={props.availableColors}
            />
        );
    };
    const renderCharmPicker = () => {
        let calendarEntry = props.calendarEntry;
        let selectedCharmId = getCharmForId(calendarEntry.CharmId);
        const { customData } = charmsDatapoints.CharmsDatapoint.charmdefaultsetdatapoint;
        logUsage(
            CHARMS_USAGE_GETDEFAULTSET,
            customData(CHARMS_USAGE_SOURCE_CALENDARPICKERCONTEXTMENU)
        );
        return (
            <div key={props.calendarEntry.calendarId + 'charmRenderer'}>
                <CharmDefaultSet
                    key="charmDefaultSet"
                    selectedIcon={selectedCharmId}
                    onSelectIcon={updateCharm}
                    color={getCurrentColorScheme()}
                    containerClassName={styles.charmContextMenuContainer}
                    trapFocus={true}
                />
            </div>
        );
    };
    const onColorClicked = (scheme: ColorScheme) => {
        let selectedColorScheme = getCalendarColorScheme(props.calendarEntry.calendarId);
        if (selectedColorScheme === scheme && props.calendarEntry.CalendarColor != -1) {
            // no change, don't fire event
            props.onDismiss();
            return;
        }
        setCalendarColor(scheme, null /* hexColorString */, props.calendarEntry);
        props.onNewColorChosen();
        props.onDismiss();
    };
    const onHexColorClicked = (hexColorString: string) => {
        if (props.calendarEntry.HexColorString === hexColorString) {
            // no change, don't fire event
            return;
        }
        setCalendarColor(null /* colorScheme */, hexColorString, props.calendarEntry);
        props.onNewColorChosen();
        props.onDismiss();
    };
    const updateCharm = (charm: CharmInfo) => {
        logUsage(
            'CalendarUpdated',
            {
                source_1: 'CalendarPicker',
            },
            {
                isCore: true,
            }
        );
        props.onDismiss();
        setCalendarCharm(charm.IconId, props.calendarEntry);
    };
    const getCurrentColorScheme = (): ColorScheme => {
        return getCalendarColorScheme(props.calendarEntry.calendarId);
    };
    const onCalendarMoveUp = () => {
        const nextCalendarId = props.prevCalendarId;
        const prevCalendarId = getPreviousOrNextCalendar(
            nextCalendarId,
            true /* shouldGetPrevious */
        );

        updateCalendarDisplayOrder(
            props.calendarEntry.calendarId.mailboxInfo.userIdentity /* userIdentity */,
            props.calendarEntry.calendarId.id /* draggedItemId */,
            prevCalendarId /* prevItemId */,
            nextCalendarId /* nextItemId */,
            props.calendarEntry.ParentGroupId /* destParentGroupId */
        );
    };
    const onCalendarMoveDown = () => {
        const prevCalendarId = props.nextCalendarId;
        const nextCalendarId = getPreviousOrNextCalendar(
            prevCalendarId,
            false /* shouldGetPrevious */
        );

        updateCalendarDisplayOrder(
            props.calendarEntry.calendarId.mailboxInfo.userIdentity /* userIdentity */,
            props.calendarEntry.calendarId.id /* draggedItemId */,
            prevCalendarId /* prevItemId */,
            nextCalendarId /* nextItemId */,
            props.calendarEntry.ParentGroupId /* destParentGroupId */
        );
    };
    const getPreviousOrNextCalendar = (calendarId: string, shouldGetPrevious: boolean) => {
        let resCalendarId: string;
        const allCalendars: CalendarEntry[] = getCalendarEntries();

        for (let i = 0; i < allCalendars.length; i++) {
            if (allCalendars[i].calendarId.id === calendarId) {
                const index = shouldGetPrevious ? i - 1 : i + 1;
                if (index >= 0 && index < allCalendars.length) {
                    const resCalendar = allCalendars[index];
                    if (
                        props.calendarEntry.ParentGroupId === resCalendar.ParentGroupId &&
                        props.calendarEntry.calendarId.mailboxInfo.userIdentity ===
                            resCalendar.calendarId.mailboxInfo.userIdentity
                    ) {
                        resCalendarId = resCalendar.calendarId.id;
                    }
                }
                break;
            }
        }

        return resCalendarId;
    };
    const onCalendarMoveTo = (destGroupId: string) => {
        updateCalendarDisplayOrder(
            props.calendarEntry.calendarId.mailboxInfo.userIdentity /* userIdentity */,
            props.calendarEntry.calendarId.id /* draggedItemId */,
            undefined /* prevItemId */,
            undefined /* nextItemId */,
            destGroupId /* destParentGroupId */
        );
    };
    const renderCalendarGroupPicker = (calendarGroups: CalendarGroup[]) => {
        const groupPickerSubMenuOptions: IContextualMenuItem[] = [];
        calendarGroups.forEach(group =>
            groupPickerSubMenuOptions.push({
                key: group.GroupName,
                name: group.GroupName,
                onClick: () => {
                    onCalendarMoveTo(group.serverGroupId);
                },
            })
        );
        return groupPickerSubMenuOptions;
    };
    const getCalendarGroupsForSubMenu = () => {
        const currGroup = props.calendarEntry.ParentGroupId;
        const userIdentity = props.calendarEntry.calendarId.mailboxInfo.userIdentity;
        return getCalendarGroups().filter(
            group =>
                group.serverGroupId !== currGroup &&
                group.calendarGroupId.mailboxInfo.userIdentity === userIdentity &&
                group.calendarGroupId.mailboxInfo.type === 'UserMailbox'
        );
    };
    let { contextMenuViewState, onDismiss } = props;
    return (
        <div className={styles.contextMenu}>
            {contextMenuViewState && (
                <ContextualMenu
                    items={populateMenuItems.get()}
                    target={contextMenuViewState.anchorElement}
                    directionalHint={DirectionalHint.bottomLeftEdge}
                    onDismiss={onDismiss}
                />
            )}
        </div>
    );
});

function renderContactsForms() {
    addBirthdayEventMenuAction();
}
